import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { getContent } from '@/api/content-api';
import { DocumentStructureSection } from '@/components/layouts/DocumentStructureSection';
import {
  useApplySavedFilter,
  useFilterDelete,
  useFilterList,
  useFilterUpdate,
  useCalendarsForMemberExternalAccounts,
  useMemberExternalAccounts,
  useNavigateWithContentFlush,
  useUser,
} from '@/hooks';
import { filterKeys } from '@/hooks/useFilterQueries';
import {
  buildCalendarSourceCatalog,
  createCalendarRouteSearchParams,
  readCalendarRouteSelection,
  toggleCalendarRouteSourceSelection,
} from '@/lib/calendar-sources';
import { getMemberDisplayName } from '@/types/member';
import { cn } from '@/lib/utils';
import { useSearchStore } from '@/store/searchStore.ts';
import { Document } from '@vivport/backend/types';
import { Filter } from '@vivport/backend/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, CalendarDays, ChevronDown, Filter as FilterIcon, PenLine, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';

const SIDEBAR_COLLAPSE_PREFS_KEY = 'layoutSidebarCollapsePrefs.v1';

const sidebarCalendarCheckboxClassName =
  'h-3.5 w-3.5 shrink-0 [&_svg]:h-2.5 [&_svg]:w-2.5';

type SidebarCollapseMode = 'general' | 'DOCUMENT';

type SidebarCollapsePrefs = {
  general: {
    calendarCollapsed: boolean;
    tabsCollapsed: boolean;
    filtersCollapsed: boolean;
  };
  document: {
    calendarCollapsed: boolean;
    tabsCollapsed: boolean;
    filtersCollapsed: boolean;
    contentsCollapsed: boolean;
  };
};

const defaultSidebarCollapsePrefs: SidebarCollapsePrefs = {
  general: {
    calendarCollapsed: true,
    tabsCollapsed: false,
    filtersCollapsed: false,
  },
  document: {
    calendarCollapsed: true,
    tabsCollapsed: true,
    filtersCollapsed: true,
    contentsCollapsed: false,
  },
};

function readSidebarCollapsePrefs(): SidebarCollapsePrefs {
  if (typeof window === 'undefined') {
    return defaultSidebarCollapsePrefs;
  }
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSE_PREFS_KEY);
    if (!raw) {
      return defaultSidebarCollapsePrefs;
    }
    const parsed = JSON.parse(raw) as Partial<SidebarCollapsePrefs>;
    return {
      general: {
        calendarCollapsed: parsed.general?.calendarCollapsed ?? defaultSidebarCollapsePrefs.general.calendarCollapsed,
        tabsCollapsed: parsed.general?.tabsCollapsed ?? defaultSidebarCollapsePrefs.general.tabsCollapsed,
        filtersCollapsed: parsed.general?.filtersCollapsed ?? defaultSidebarCollapsePrefs.general.filtersCollapsed,
      },
      document: {
        calendarCollapsed: parsed.document?.calendarCollapsed ?? defaultSidebarCollapsePrefs.document.calendarCollapsed,
        tabsCollapsed: parsed.document?.tabsCollapsed ?? defaultSidebarCollapsePrefs.document.tabsCollapsed,
        filtersCollapsed: parsed.document?.filtersCollapsed ?? defaultSidebarCollapsePrefs.document.filtersCollapsed,
        contentsCollapsed: parsed.document?.contentsCollapsed ?? defaultSidebarCollapsePrefs.document.contentsCollapsed,
      },
    };
  } catch {
    return defaultSidebarCollapsePrefs;
  }
}

function writeSidebarCollapsePrefs(prefs: SidebarCollapsePrefs): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSE_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // ignore storage write failures
  }
}

export function LayoutSidebar() {
  const location = useLocation();
  const navigateFromSidebar = useNavigateWithContentFlush();
  const queryClient = useQueryClient();
  const { data: memberContext } = useUser();
  const filterListQuery = useFilterList();
  const filterUpdateMutation = useFilterUpdate();
  const filterDeleteMutation = useFilterDelete();
  const accountTabs = memberContext?.accountTabs || [];
  const currentMemberId = memberContext?.selectedMember.id;
  const externalAccountsQuery = useMemberExternalAccounts(currentMemberId ?? 0);
  const calendarExternalAccountIds = useMemo(
    () =>
      (externalAccountsQuery.data?.items ?? [])
        .filter((item) => item.provider === 'GOOGLE' && item.isActive)
        .map((item) => item.id),
    [externalAccountsQuery.data?.items]
  );
  const calendarsQuery = useCalendarsForMemberExternalAccounts(calendarExternalAccountIds);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [editingFilterId, setEditingFilterId] = useState<number | null>(null);
  const [calendarCollapsed, setCalendarCollapsed] = useState(defaultSidebarCollapsePrefs.general.calendarCollapsed);
  const [tabsCollapsed, setTabsCollapsed] = useState(defaultSidebarCollapsePrefs.general.tabsCollapsed);
  const [filtersCollapsed, setFiltersCollapsed] = useState(defaultSidebarCollapsePrefs.general.filtersCollapsed);
  const [contentsCollapsed, setContentsCollapsed] = useState(defaultSidebarCollapsePrefs.document.contentsCollapsed);
  const {
    activeSavedFilterId,
    clearActiveSavedFilter,
    isCurrentTab,
    setActiveSavedFilter,
    setInputValue,
    setQuery
  } = useSearchStore();
  const savedFilters = useMemo(() => {
    const savedFilterIds = filterListQuery.data?.ids ?? [];
    return savedFilterIds
      .map((id) => queryClient.getQueryData(filterKeys.detail(id)) as Filter | undefined)
      .filter((item): item is Filter => item !== undefined);
  }, [filterListQuery.data?.ids, queryClient]);

  const contentRouteMatch = useMatch('/content/:contentRef');
  const contentRef = contentRouteMatch?.params.contentRef ?? null;
  const contentDetailQuery = useQuery({
    queryKey: ['content-ref', contentRef],
    queryFn: () => getContent(contentRef as string),
    enabled: Boolean(contentRef),
  });
  const currentDocument = useMemo((): Document | null => {
    if (!contentRef || !contentDetailQuery.data) {
      return null;
    }

    if (contentDetailQuery.data.contentType !== 'DOCUMENT') {
      return null;
    }

    return contentDetailQuery.data as Document;
  }, [contentDetailQuery.data, contentRef]);
  const isDocumentDetailRoute = Boolean(currentDocument);
  const sidebarMode: SidebarCollapseMode = isDocumentDetailRoute ? 'DOCUMENT' : 'general';

  useEffect(() => {
    const prefs = readSidebarCollapsePrefs();
    if (sidebarMode === 'DOCUMENT') {
      setCalendarCollapsed(true);
      setTabsCollapsed(true);
      setFiltersCollapsed(true);
      setContentsCollapsed(prefs.document.contentsCollapsed);
      return;
    }
    setCalendarCollapsed(prefs.general.calendarCollapsed);
    setTabsCollapsed(prefs.general.tabsCollapsed);
    setFiltersCollapsed(prefs.general.filtersCollapsed);
    setContentsCollapsed(defaultSidebarCollapsePrefs.document.contentsCollapsed);
  }, [sidebarMode]);

  const persistModeCollapseState = (
    mode: SidebarCollapseMode,
    nextValues: {
      calendarCollapsed?: boolean;
      tabsCollapsed?: boolean;
      filtersCollapsed?: boolean;
      contentsCollapsed?: boolean;
    }
  ) => {
    const prefs = readSidebarCollapsePrefs();
    const nextPrefs: SidebarCollapsePrefs =
      mode === 'DOCUMENT'
        ? {
            ...prefs,
            document: {
              calendarCollapsed: nextValues.calendarCollapsed ?? calendarCollapsed,
              tabsCollapsed: nextValues.tabsCollapsed ?? tabsCollapsed,
              filtersCollapsed: nextValues.filtersCollapsed ?? filtersCollapsed,
              contentsCollapsed: nextValues.contentsCollapsed ?? contentsCollapsed,
            },
          }
        : {
            ...prefs,
            general: {
              calendarCollapsed: nextValues.calendarCollapsed ?? calendarCollapsed,
              tabsCollapsed: nextValues.tabsCollapsed ?? tabsCollapsed,
              filtersCollapsed: nextValues.filtersCollapsed ?? filtersCollapsed,
            },
          };
    writeSidebarCollapsePrefs(nextPrefs);
  };

  const canEditFilter = (filter: Filter): boolean => {
    if (!currentMemberId) return false;
    if (filter.ownerScope === 'MEMBER') {
      return filter.ownerMemberId === currentMemberId;
    }
    return memberContext?.isHubAdmin ?? false;
  };

  const applySavedFilterView = useApplySavedFilter();
  const applySavedFilter = (filter: Filter): void => {
    applySavedFilterView(filter, navigateFromSidebar, '/explore');
  };

  const openEditDialog = (filter: Filter): void => {
    setEditingFilterId(filter.id);
    setSaveName(filter.name);
    setSaveDescription(filter.description ?? '');
    setSaveDialogOpen(true);
  };

  const saveEditedFilter = (): void => {
    if (!editingFilterId) return;
    const editedFilterId = editingFilterId;
    const activeFilter = savedFilters.find((filter) => filter.id === editedFilterId);
    if (!activeFilter) return;

    filterUpdateMutation.mutate(
      {
        id: editedFilterId,
        data: {
          name: saveName.trim(),
          description: saveDescription.trim() || undefined,
          definition: activeFilter.definition
        }
      },
      {
        onSuccess: (updated) => {
          if (activeSavedFilterId === editedFilterId) {
            setActiveSavedFilter(updated.id, updated.definition, updated.name);
          }
          setSaveDialogOpen(false);
        },
        onError: (error) => {
          console.error('Saved filter update failed:', error);
        }
      }
    );
  };

  const handleDeleteFilter = (filter: Filter): void => {
    const confirmed = window.confirm(`Delete saved filter "${filter.name}"? This action cannot be undone.`);
    if (!confirmed) {
      return;
    }

    filterDeleteMutation.mutate(filter.id, {
      onSuccess: () => {
        if (activeSavedFilterId === filter.id) {
          clearActiveSavedFilter();
        }
      },
      onError: (error) => {
        console.error('Saved filter delete failed:', error);
      }
    });
  };

  const calendarSourceCatalog = useMemo(() => {
    if (!memberContext) {
      return null;
    }
    return buildCalendarSourceCatalog({
      selectedMember: memberContext.selectedMember,
      calendarsByAccount: calendarsQuery.data,
    });
  }, [calendarsQuery.data, memberContext]);

  const isCalendarRoute = location.pathname.startsWith('/calendar');
  const calendarRouteSelection = useMemo(
    () => readCalendarRouteSelection(new URLSearchParams(location.search), calendarSourceCatalog),
    [calendarSourceCatalog, location.search]
  );

  const handleCalendarSourceClick = (): void => {
    if (isCalendarRoute) {
      return;
    }
    const nextParams = createCalendarRouteSearchParams(calendarRouteSelection);
    const nextQuery = nextParams.toString();
    navigateFromSidebar(nextQuery.length > 0 ? `/calendar?${nextQuery}` : '/calendar');
  };

  const handleCalendarSourceVisibilityToggle = (sourceId: string): void => {
    if (!calendarSourceCatalog) {
      return;
    }
    const nextSelection = toggleCalendarRouteSourceSelection(
      calendarRouteSelection,
      sourceId,
      calendarSourceCatalog.defaultSource.id
    );
    const nextParams = createCalendarRouteSearchParams(nextSelection);
    const nextQuery = nextParams.toString();
    navigateFromSidebar(nextQuery.length > 0 ? `/calendar?${nextQuery}` : '/calendar');
  };
  const handleCalendarHubClashesToggle = (): void => {
    const nextSelection = {
      ...calendarRouteSelection,
      showHubClashes: !calendarRouteSelection.showHubClashes,
    };
    const nextParams = createCalendarRouteSearchParams(nextSelection);
    const nextQuery = nextParams.toString();
    navigateFromSidebar(nextQuery.length > 0 ? `/calendar?${nextQuery}` : '/calendar');
  };

  const selectedMemberCalendarSources = calendarSourceCatalog?.selectedSources ?? [];
  const hasSelectedMemberCalendars = selectedMemberCalendarSources.length > 0;
  const selectedMemberCalendarEmptyStateLabel = useMemo(() => {
    if (!memberContext) {
      return 'selected member';
    }
    return memberContext.selectedMember.isPersonal
      ? 'you'
      : getMemberDisplayName(memberContext.selectedMember);
  }, [memberContext]);

  return (
    <nav className="space-y-1 w-full">
      <div data-testid="sidebar-section-calendar" className="pb-3">
        <button
          data-testid="sidebar-toggle-calendar"
          type="button"
          aria-expanded={!calendarCollapsed}
          className="flex w-full items-center justify-between pl-2 pr-0 pb-1 text-sm font-medium text-muted-foreground"
          onClick={() =>
            setCalendarCollapsed((previous) => {
              const next = !previous;
              persistModeCollapseState(sidebarMode, { calendarCollapsed: next });
              return next;
            })
          }
        >
          <span>Calendar</span>
          <span className="flex h-7 w-7 items-center justify-center -mr-1">
            <ChevronDown className={cn('h-4 w-4 transition-transform', calendarCollapsed && '-rotate-90')} />
          </span>
        </button>
        {!calendarCollapsed ? (
          <div data-testid="sidebar-calendar-list" className="space-y-1">
          {hasSelectedMemberCalendars ? (
            selectedMemberCalendarSources.map((source) => {
              const isVisible = calendarRouteSelection.selectedMemberCalendarSourceIds.includes(source.id);
              const isLastVisible = isVisible && calendarRouteSelection.selectedMemberCalendarSourceIds.length === 1;
              const rowClassName = cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-muted-foreground',
                !isCalendarRoute && 'hover:bg-accent'
              );
              const rowContent = (
                <>
                  <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{source.label}</span>
                  </div>
                  <Checkbox
                    className={sidebarCalendarCheckboxClassName}
                    checked={isVisible}
                    disabled={isLastVisible}
                    aria-label={`Toggle ${source.label}`}
                    onClick={(event) => event.stopPropagation()}
                    onCheckedChange={() => handleCalendarSourceVisibilityToggle(source.id)}
                  />
                </>
              );
              return (
                isCalendarRoute ? (
                  <div
                    key={source.id}
                    data-calendar-source-id={source.id}
                    data-testid="sidebar-calendar-item"
                    data-active={isVisible ? 'true' : undefined}
                    className={rowClassName}
                  >
                    {rowContent}
                  </div>
                ) : (
                  <div
                    key={source.id}
                    data-calendar-source-id={source.id}
                    data-testid="sidebar-calendar-item"
                    data-active={isVisible ? 'true' : undefined}
                    className={rowClassName}
                    onClick={handleCalendarSourceClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') {
                        return;
                      }
                      event.preventDefault();
                      handleCalendarSourceClick();
                    }}
                  >
                    {rowContent}
                  </div>
                )
              );
            })
          ) : (
            <div className="space-y-2 rounded-md px-3 py-2">
              <p className="text-xs text-muted-foreground">
                No readable calendars are connected for {selectedMemberCalendarEmptyStateLabel}.
              </p>
            </div>
          )}
          <div className="px-3">
            <TooltipProvider delayDuration={400}>
              <div className="flex w-full items-center gap-3 rounded-md py-2 text-left text-muted-foreground">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex min-w-0 flex-1 cursor-default items-center gap-3">
                      <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate text-sm">Show Hub Clashes</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[220px] text-xs">
                    Overlay hub-level clashes while viewing selected member calendars.
                  </TooltipContent>
                </Tooltip>
                <Checkbox
                  className={sidebarCalendarCheckboxClassName}
                  checked={calendarRouteSelection.showHubClashes}
                  aria-label="Show Hub Clashes"
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={handleCalendarHubClashesToggle}
                />
              </div>
            </TooltipProvider>
          </div>
          </div>
        ) : null}
      </div>

      <button
        data-testid="sidebar-toggle-tabs"
        type="button"
        aria-expanded={!tabsCollapsed}
        className="flex w-full items-center justify-between pl-2 pr-0 pb-1 text-sm font-medium text-muted-foreground"
        onClick={() =>
          setTabsCollapsed((previous) => {
            const next = !previous;
            persistModeCollapseState(sidebarMode, { tabsCollapsed: next });
            return next;
          })
        }
      >
        <span>Tabs</span>
        <span className="flex h-7 w-7 items-center justify-center -mr-1">
          <ChevronDown className={cn('h-4 w-4 transition-transform', tabsCollapsed && '-rotate-90')} />
        </span>
      </button>
      {!tabsCollapsed ? (
        <div data-testid="sidebar-tabs" className="space-y-1">
          {accountTabs.length === 0 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigateFromSidebar('/work4me/filters');
              }}
              className="w-full py-2"
            >
              Add Tabs
            </Button>
          ) : (
          accountTabs.map(
            (
              tab: { id?: number; label: string; labelMobile: string; searchFilter: string },
              index: number
            ) => (
              <div
                key={index}
                data-testid="sidebar-tab-item"
                data-tab-search-filter={tab.searchFilter}
                data-active={location.pathname.includes('explore') && isCurrentTab(tab.searchFilter) ? 'true' : undefined}
                className={cn(
                  'flex items-center px-3 py-2 space-x-3 rounded-md cursor-pointer',
                  location.pathname.includes('explore') && isCurrentTab(tab.searchFilter)
                    ? 'bg-muted text-primary border-l-4 border-primary'
                    : 'hover:bg-accent text-muted-foreground'
                )}
                onClick={() => {
                  if (tab.searchFilter) {
                    clearActiveSavedFilter();
                    setQuery(tab.searchFilter);
                    setInputValue(tab.searchFilter, navigateFromSidebar, '/explore');
                    return;
                  }
                  const tabFilter = (memberContext?.tabFilters ?? []).find((f) => f.id === tab.id);
                  if (tabFilter) {
                    applySavedFilter(tabFilter);
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 text-muted-foreground"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                  />
                </svg>
                <span className="truncate text-sm md:hidden">
                  {tab.labelMobile}
                </span>
                <span className="truncate text-sm hidden md:inline">
                  {tab.label}
                </span>
              </div>
            )
          )
          )}
        </div>
      ) : null}

      {/* Work4Me Button */}
      <div className="pt-2 px-4 flex justify-center">
        <Button
          data-testid="sidebar-work4me-button"
          size="lg"
          onClick={() => {
            navigateFromSidebar('/work4me/agents');
          }}
          className="w-48 rounded-full bg-background text-muted-foreground border border-muted-foreground shadow-lg hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <span className="px-3 retro-2000s-font text-lg">Work4Me</span>
        </Button>
      </div>

      <button
        data-testid="sidebar-toggle-filters"
        type="button"
        aria-expanded={!filtersCollapsed}
        className="flex w-full items-center justify-between pt-3 pl-2 pr-0 pb-1 text-sm font-medium text-muted-foreground"
        onClick={() =>
          setFiltersCollapsed((previous) => {
            const next = !previous;
            persistModeCollapseState(sidebarMode, { filtersCollapsed: next });
            return next;
          })
        }
      >
        <span>Filters</span>
        <span className="flex h-7 w-7 items-center justify-center -mr-1">
          <ChevronDown className={cn('h-4 w-4 transition-transform', filtersCollapsed && '-rotate-90')} />
        </span>
      </button>
      {!filtersCollapsed ? (
        <div data-testid="sidebar-filter-list" className="space-y-1">
          {savedFilters.map((filter) => {
            const isActive = location.pathname.includes('explore') && activeSavedFilterId === filter.id;
            const canEdit = canEditFilter(filter);
            return (
              <div
                key={filter.id}
                data-testid="sidebar-filter-item"
                data-filter-name={filter.name}
                data-active={isActive ? 'true' : undefined}
                className={cn(
                  'group flex items-center rounded-md px-3 py-2',
                  isActive
                    ? 'bg-muted text-primary border-l-4 border-primary'
                    : 'hover:bg-accent text-muted-foreground'
                )}
              >
                <button
                  type="button"
                  className="min-w-0 flex flex-1 items-center gap-1.5 text-left"
                  onClick={() => applySavedFilter(filter)}
                >
                  <FilterIcon className="w-4 h-4 shrink-0 text-muted-foreground" />
                  {filter.ownerScope === 'HUB' && (
                    <span className="flex items-center shrink-0 text-muted-foreground" title="Hub filter">
                      <Building2 className="h-3.5 w-3.5" />
                    </span>
                  )}
                  <span className="truncate text-sm hidden md:inline">{filter.name}</span>
                  <span className="truncate text-sm md:hidden">{filter.name}</span>
                </button>
                {canEdit && (
                  <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => openEditDialog(filter)}
                      aria-label={`Edit filter ${filter.name}`}
                    >
                      <PenLine className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleDeleteFilter(filter)}
                      aria-label={`Delete filter ${filter.name}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {currentDocument ? (
        <DocumentStructureSection
          document={currentDocument}
          collapsed={contentsCollapsed}
          onToggleCollapsed={() =>
            setContentsCollapsed((previous) => {
              const next = !previous;
              persistModeCollapseState('DOCUMENT', { contentsCollapsed: next });
              return next;
            })
          }
        />
      ) : null}

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Saved Filter</DialogTitle>
            <DialogDescription>
              Update this saved filter metadata.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <Input
              placeholder="Filter name"
              value={saveName}
              onChange={(event) => setSaveName(event.target.value)}
            />
            <Input
              placeholder="Description (optional)"
              value={saveDescription}
              onChange={(event) => setSaveDescription(event.target.value)}
            />
          </DialogBody>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!saveName.trim() || filterUpdateMutation.isPending}
              onClick={saveEditedFilter}
            >
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </nav>
  );
}


